import { FC, useCallback, useEffect, useRef } from 'react';

import { twMerge } from 'tailwind-merge';

import { Skeleton } from '../../skeleton';
import { Spinner } from '../../spinner';
import { TInfinityScroll } from './types';

export const InfinityScroll: FC<TInfinityScroll> = ({
  children,
  onLoadMore,
  hasMore,
  isLoadingMore = false,
  isLoading = false,
  CustomLoader,
  CustomSkeletons,
  threshold = '100px',
  className,
  loaderClassName,
}) => {
  const observerTarget = useRef<HTMLDivElement>(null);
  const hasTriggeredRef = useRef(false);

  const handleLoadMore = useCallback(async () => {
    if (
      hasMore &&
      !isLoadingMore &&
      !isLoading &&
      !hasTriggeredRef.current &&
      onLoadMore
    ) {
      hasTriggeredRef.current = true;
      try {
        await onLoadMore();
      } finally {
        hasTriggeredRef.current = false;
      }
    }
  }, [hasMore, isLoadingMore, isLoading, onLoadMore]);

  useEffect(() => {
    const target = observerTarget.current;
    if (!target || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void handleLoadMore();
        }
      },
      {
        root: null,
        rootMargin: threshold,
        threshold: 0.1,
      },
    );

    observer.observe(target);

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [handleLoadMore, hasMore, threshold]);

  if (isLoading) {
    return (
      <div className={twMerge('w-full', className)}>
        {CustomSkeletons || <Skeleton className="h-4 w-full" />}
      </div>
    );
  }

  return (
    <div className={twMerge('h-full w-full overflow-y-auto', className)}>
      {children}

      {hasMore && onLoadMore && (
        <div ref={observerTarget} className={twMerge(loaderClassName)}>
          {isLoadingMore &&
            (CustomLoader || <Spinner size="sm" className="mx-auto" />)}
        </div>
      )}
    </div>
  );
};
